import { noop } from "es-toolkit"
import {
  type ActorRefFrom,
  assertEvent,
  assign,
  setup,
  type SnapshotFrom,
} from "xstate"

import { unique } from "@/helpers/other"
import { isDefined } from "@/helpers/typeguards"
import callDurationPersistenceService from "@/services/callDurationPersistenceService"
import { contactStorageService } from "@/services/localStorageService"

import { pseudoACWStatus } from "./connect-listeners"
import { persistenceService } from "./persistence"
import { getTaskOrContactId } from "./state-helpers"
import type { AgentEvent, AgentState } from "./state-types"

export const rootMachine = setup({
  types: {
    context: {} as AgentState,
    events: {} as AgentEvent,
  },
  actions: {
    syncConnectStatus: assign(({ event }) => {
      assertEvent(event, "CHANGE_CONNECT_AGENT_STATUS")

      return {
        connectAgentStatus: event.status,
      }
    }),
    initializeCallTimerStateFromPersistence: assign(({ context }) => {
      const contactId = getTaskOrContactId(context) // contact might not be available here yet
      const callDurationData =
        callDurationPersistenceService.loadInitialCallDuration(contactId)

      return {
        durationData: {
          callContactId: contactId,
          callStartTimestamp: callDurationData.callStartTimestamp,
          callEndTimestamp: callDurationData.callEndTimestamp,
        },
      }
    }),
    startCallTimer: assign(({ context }) => {
      const contactId = getTaskOrContactId(context)
      const startTime = new Date().toISOString()

      if (contactId) {
        callDurationPersistenceService.saveCallStart(contactId, startTime)
      }

      return {
        durationData: {
          callContactId: contactId,
          callStartTimestamp: startTime,
          callEndTimestamp: null,
        },
      }
    }),
    stopCallTimer: assign(({ context }) => {
      const contactId =
        getTaskOrContactId(context) || context.durationData?.callContactId

      if (!context.durationData?.callStartTimestamp || !contactId) {
        return {}
      }

      const endTime = new Date().toISOString()
      callDurationPersistenceService.saveCallEnd(
        contactId,
        context.durationData.callStartTimestamp,
        endTime,
      )

      return {
        durationData: {
          ...context.durationData,
          callEndTimestamp: endTime,
        },
      }
    }),
    setOutboundVoiceContactId: assign(({ context, event }) => {
      assertEvent(event, "NEW_OUTBOUND_VOICE_CONTACT_ID")

      return {
        taskMeta: context.taskMeta
          ? {
              ...context.taskMeta,
              voiceId: event.contactId,
            }
          : undefined,
      }
    }),
    setVoiceIdForCallDuration: assign(({ context, event }) => {
      assertEvent(event, "NEW_OUTBOUND_VOICE_CONTACT_ID")

      // in manual call, set voiceId in durationData
      const voiceContactId = event.contactId
      if (voiceContactId && context.durationData?.callStartTimestamp) {
        callDurationPersistenceService.saveCallStart(
          voiceContactId,
          context.durationData.callStartTimestamp,
        )
      }

      return {
        durationData: {
          callContactId: voiceContactId,
          callStartTimestamp: context.durationData?.callStartTimestamp || null,
          callEndTimestamp: context.durationData?.callEndTimestamp || null,
        },
      }
    }),
    setTaskMeta: assign(({ event }, params?: { wasInterrupted?: boolean }) => {
      assertEvent(event, ["NEW_TASK"])

      return {
        taskMeta: {
          ...event.task,
          wasInterrupted: params?.wasInterrupted,
        },
      }
    }),
    setTaskData: assign(({ event }) => {
      assertEvent(event, ["TASK_DATA_LOADED"])

      return {
        taskData: event.task,
      }
    }),
    updateCaseChannels: assign(({ context, event }) => {
      assertEvent(event, ["CASE_CHANNELS_REFRESHED"])
      if (!context.taskData) {
        return {}
      }

      return {
        taskData: {
          ...context.taskData,
          caseData: {
            ...context.taskData.caseData,
            case: {
              ...context.taskData.caseData.case,
              processed_data: {
                selected_channels: event.selectedChannels,
              },
            },
          },
        },
      }
    }),
    // When leaving the busy state, reset everything
    resetTask: assign({
      taskData: undefined,
      taskMeta: undefined,
      durationData: {
        callStartTimestamp: null,
        callEndTimestamp: null,
      },
    }),
    saveCallReport: assign(({ event }) => {
      assertEvent(event, "SAVE_CALL_REPORT")

      return { historyItem: event.callReport }
    }),
    resetCallReport: assign(() => ({ historyItem: null })),
    toggleDisplayStatusOffline: assign({ displayStatus: "offline" }),
    // Side effects to persist the agent state when reloading the page
    setManualCall: assign(() => {
      persistenceService.makeCall(true)

      return {
        persistedContext: persistenceService.read(),
      }
    }),
    makeCallAttempt: assign(() => {
      persistenceService.makeCall(false)

      return {
        persistedContext: persistenceService.read(),
      }
    }),
    resetPersistedState: assign(() => {
      persistenceService.reset()

      return {
        persistedContext: persistenceService.read(),
      }
    }),
    resetManualCallCase: () => {
      contactStorageService.removeManualCallCase()
    },
    pushNetworkingError: assign(({ context, event }) => {
      assertEvent(event, "SET_NETWORKING_ERROR")

      const newErrors = unique([...context.networkingErrors, event.error_kind])

      return {
        networkingErrors: newErrors,
      }
    }),
    removeNetworkingError: assign(({ context, event }) => {
      assertEvent(event, "RESET_NETWORKING_ERROR")

      const newErrors = context.networkingErrors.filter(
        (err) => err !== event.error_kind,
      )

      return {
        networkingErrors: newErrors,
      }
    }),
    // Side effects to manipulate the agent status
    // implemented using machine.provide(implementations)
    enterPseudoACW: noop,
  },
  // Guards are used as the conditions to trigger state transitions, based on the current Connect agent status
  guards: {
    isConnectOnline: ({ context, event }) => {
      assertEvent(event, "CHANGE_CONNECT_AGENT_STATUS")

      const inManualCall = context.persistedContext.isManualCall

      return !inManualCall && event.status !== "Offline"
    },
    isConnectOffline: ({ context, event }) => {
      assertEvent(event, "CHANGE_CONNECT_AGENT_STATUS")

      const inManualCall = context.persistedContext.isManualCall

      return !inManualCall && event.status === "Offline"
    },
    isOffline: ({ context }) => context.displayStatus === "offline",
    isAgentCalling: ({ context: { connectAgentStatus } }) =>
      connectAgentStatus === "CallingCustomer",
    isStudentCalling: ({ context, event }) => {
      return (
        context.connectAgentStatus === "PendingBusy" ||
        (event.type === "CHANGE_CONNECT_AGENT_STATUS" &&
          event.status === "PendingBusy")
      )
    },
    isInCallEvent: ({ event }) => {
      assertEvent(event, "CHANGE_CONNECT_AGENT_STATUS")

      return event.status === "Busy"
    },
    isInCallState: ({ context }) => context.connectAgentStatus === "Busy",
    isFailedCallEvent: ({ event }) => {
      assertEvent(event, "CHANGE_CONNECT_AGENT_STATUS")
      const { status } = event

      return (
        pseudoACWStatus.includes(status) ||
        // take into accounts some direct transitions from `CallingCustomer` to `Offline` or `Available` when the student does not pick the phone
        status === "Offline" ||
        status === "Available"
      )
    },
    isMissedInboundCallEvent: ({ event }) => {
      assertEvent(event, "CHANGE_CONNECT_AGENT_STATUS")

      return event.status === "MissedCallAgent"
    },
    isMissedCall: ({ context: { connectAgentStatus } }) =>
      connectAgentStatus === "MissedCallAgent",
    isACW: ({ context: { connectAgentStatus } }) =>
      connectAgentStatus === "AfterCallWork",
    isLoadedInPseudoACW: ({ context }) => context.persistedContext.isPseudoACW,
    isLoadedManualCall: ({ context }) => context.persistedContext.isManualCall,
    isMissedTask: ({ context: { taskMeta } }) => {
      return taskMeta?.connectContactStatus === "missed"
    },
    // Contact error events transitions
    isRejectedTask: ({ event }) => {
      assertEvent(event, "CONTACT_ERROR")

      return event.status === "rejected" && event.isTask
    },
    isRejectedCall: ({ event }) => {
      assertEvent(event, "CONTACT_ERROR")

      return event.status === "rejected" && !event.isTask
    },
    hasTaskMeta: ({ context }) => isDefined(context.taskMeta),
  },
}).createMachine({
  id: "root",
  context: {
    agentProfile: null,
    connectAgentStatus: "Init",
    displayStatus: "loading",
    persistedContext: persistenceService.read(),
    historyItem: null,
    networkingErrors: [],
    durationData: {
      callStartTimestamp: null,
      callEndTimestamp: null,
    },
  },
  initial: "loading",
  on: {
    // Keep track of any change of the agent status in Connect
    CHANGE_CONNECT_AGENT_STATUS: {
      actions: "syncConnectStatus",
    },
    AGENT_LOADED: {
      actions: assign({
        queueNames: ({ event }) => event.agentData.queueNames,
      }),
    },
    AGENT_PROFILE_LOADED: {
      actions: assign({
        agentProfile: ({ event }) => event.agentProfile,
      }),
    },
    SET_NETWORKING_ERROR: {
      actions: "pushNetworkingError",
    },
    RESET_NETWORKING_ERROR: {
      actions: "removeNetworkingError",
    },
  },
  states: {
    loading: {
      on: {
        CHANGE_CONNECT_AGENT_STATUS: [
          {
            guard: "isLoadedManualCall",
            target: "manualCallRestore",
            actions: [
              "syncConnectStatus",
              assign(() => ({ displayStatus: "offline" })),
            ],
          },
          {
            guard: "isConnectOffline",
            target: "offline",
            actions: [
              assign(() => ({ displayStatus: "offline" })),
              "syncConnectStatus",
            ],
          },
          {
            guard: "isConnectOnline",
            target: "available",
            actions: [
              assign(() => ({ displayStatus: "online" })),
              "syncConnectStatus",
            ],
          },
        ],
        AGENT_LOADED: {
          actions: assign({
            queueNames: ({ event }) => event.agentData.queueNames,
          }),
        },
      },
    },
    offline: {
      always: [
        {
          guard: "isAgentCalling",
          target: "busy.outbound_ringing",
          actions: ["setManualCall", "startCallTimer"],
        },
        { guard: "isLoadedInPseudoACW", target: "busy.after_call.pseudo_acw" }, // reloading the page after a failed manual call
      ],
      on: {
        GO_ONLINE: {
          target: "available",
          actions: assign(() => ({ displayStatus: "online" })),
        },
      },
    },
    available: {
      entry: assign(() => ({ task: undefined, taskData: undefined })),
      on: {
        NEW_TASK: {
          target: "busy.loading_task",
          actions: [{ type: "setTaskMeta" }],
        },
        GO_OFFLINE: {
          target: "offline",
          actions: [
            assign(() => {
              return { displayStatus: "offline" }
            }),
          ],
        },
      },
    },
    busy: {
      initial: "loading_task",
      exit: ["resetTask", "resetPersistedState", "resetCallReport"],
      on: {
        SAVE_CALL_REPORT: { actions: "saveCallReport" },
        CASE_CHANNELS_REFRESHED: { actions: "updateCaseChannels" },
        // Take into account various errors from Contact
        CONTACT_ERROR: [
          { target: "busy.error.rejected_task", guard: "isRejectedTask" },
          { target: "busy.error.rejected_call", guard: "isRejectedCall" },
        ],
        // Makes sure we write the voice call id whenever an outbound call is made
        NEW_OUTBOUND_VOICE_CONTACT_ID: [
          { guard: "hasTaskMeta", actions: "setOutboundVoiceContactId" },
          { guard: "isLoadedManualCall", actions: "setVoiceIdForCallDuration" },
        ],
      },
      states: {
        loading_task: {
          on: {
            TASK_DATA_LOADED: [
              {
                guard: "isLoadedInPseudoACW",
                target: "after_call.pseudo_acw",
                actions: [
                  "setTaskData",
                  "initializeCallTimerStateFromPersistence",
                ],
              },
              {
                guard: "isACW",
                target: "after_call.normal_acw",
                actions: [
                  "setTaskData",
                  "initializeCallTimerStateFromPersistence",
                ],
              },
              {
                guard: "isStudentCalling",
                target: "inbound_ringing",
                actions: ["setTaskData"],
              },
              {
                guard: "isMissedCall",
                target: "error.missed_inbound_call",
                actions: ["setTaskData"],
              },
              // specific case: a task is loaded with the status `missed`
              {
                guard: "isMissedTask",
                target: "error.missed_task",
                actions: ["setTaskData"],
              },
              // Handle the case when agent refreshes the page while in a connected voice call
              // Or refreshes page after websocket connection error
              {
                guard: ({ context }) => context.connectAgentStatus === "Busy",
                target: "in_call",
                actions: [
                  "setTaskData",
                  "initializeCallTimerStateFromPersistence",
                ],
              },
              {
                guard: "isAgentCalling",
                target: "outbound_ringing",
                actions: [
                  "setTaskData",
                  "initializeCallTimerStateFromPersistence",
                ],
              },
              // default case, pre_call state with CALL / WON'T CALL
              {
                target: "pre_call",
                actions: ["setTaskData"],
              },
            ],
            ENTER_RESET_MODE: { target: "reset" },
            NEW_TASK: {
              target: "loading_task",
              actions: [
                { type: "setTaskMeta", params: { wasInterrupted: true } },
              ],
            },
          },
        },
        reset: {
          on: {
            LEAVE_RESET_MODE: { target: "#root.available" },
          },
        },
        pre_call: {
          initial: "normal",
          states: {
            normal: {
              on: {
                WONT_CALL: { target: "no_call" },
              },
            },
            no_call: {
              on: {
                GO_BACK_TO_CALL_MODE: {
                  target: "normal",
                  actions: ["resetCallReport"],
                },
                GO_NEXT: { target: "#root.available" },
              },
            },
          },
          always: [
            {
              guard: "isAgentCalling",
              target: "outbound_ringing",
              actions: ["makeCallAttempt", "startCallTimer"],
            },
          ],
          on: {
            GO_OFFLINE: {
              target: "#root.offline", // `Confirm and Go offline` flow
              actions: ["toggleDisplayStatusOffline"],
            },
            NEW_TASK: {
              target: "loading_task",
              actions: [
                { type: "setTaskMeta", params: { wasInterrupted: true } },
              ],
            },
          },
        },
        outbound_ringing: {
          on: {
            CHANGE_CONNECT_AGENT_STATUS: [
              { guard: "isInCallEvent", target: "in_call" },
              {
                target: "after_call.pseudo_acw", // default transition (no guard needed)
                actions: ["stopCallTimer"], // Stop timer if call fails to connect properly
              },
            ],
          },
        },
        inbound_ringing: {
          on: {
            REJECT_INBOUND_CALL: { target: "#root.available" },
            CHANGE_CONNECT_AGENT_STATUS: [
              {
                guard: "isInCallEvent",
                target: "in_call",
                actions: ["startCallTimer"],
              },
              {
                guard: "isMissedInboundCallEvent",
                target: "error.missed_inbound_call",
              },
              {
                guard: "isFailedCallEvent",
                target: "#root.available", // go back to available state if the student ends the call before the agent picks up
              },
            ],
          },
        },
        in_call: {
          always: [
            {
              guard: "isACW",
              target: "after_call.normal_acw",
              actions: ["stopCallTimer"],
            },
          ],
        },
        after_call: {
          on: {
            GO_NEXT: [
              { guard: "isOffline", target: "#root.offline" }, // after manual call, back to `Offline` state
              { target: "#root.available" }, // `Confirm and Go Next` flow
            ],
            GO_OFFLINE: {
              target: "#root.offline", // `Confirm and Go offline` flow
              actions: ["toggleDisplayStatusOffline"],
            },
          },
          initial: "normal_acw",
          states: {
            normal_acw: {},
            pseudo_acw: {
              entry: ["enterPseudoACW"], // Connect status will be reset by GO_NEXT/GO_OFFLINE side effects
            },
          },
          exit: ["resetManualCallCase"],
        },
        error: {
          on: {
            CLOSE_TASK_AFTER_ERROR: { target: "#root.available" },
            // Going offline while in an error state such as missed call or missed task
            GO_OFFLINE: {
              target: "#root.offline",
              actions: "toggleDisplayStatusOffline",
            },
          },
          initial: "internal_error",
          states: {
            missed_inbound_call: {},
            missed_call_agent_state: {}, // probably a network error, introduced here: https://github.com/scoville/cci-call-panel/pull/381
            missed_task: {},
            internal_error: {},
            rejected_task: {},
            rejected_call: {},
          },
        },
      },
    },
    manualCallRestore: {
      // this is a temporary state to restore the agent state when the page is refreshed while the agent is in a manual call
      entry: ["initializeCallTimerStateFromPersistence"], // Ensure timer state is loaded
      always: [
        {
          guard: "isAgentCalling",
          target: "busy.outbound_ringing",
        },
        {
          guard: "isInCallState",
          target: "busy.in_call",
        },
        {
          guard: "isACW",
          target: "busy.after_call.normal_acw",
        },
        {
          guard: "isLoadedInPseudoACW",
          target: "busy.after_call.pseudo_acw",
        },
      ],
    },
  },
})

export type AgentStateMachineRef = ActorRefFrom<typeof rootMachine>

export type AgentMachineSnapshot = SnapshotFrom<typeof rootMachine>
